import { Box, Stack } from "@mui/material";
import { type GoalOverview, GoalOverviewProgressStatus } from "Infrastructure/ApiZfp/Schema";
import { useResponsive } from "Infrastructure/Hooks/useResponsive";
import { Radius, Spacing, TABLET_MAX_WIDTH } from "Infrastructure/Themes/Themes";
import { useResource } from "Infrastructure/Translations/Resources";
import { GOAL_CARD_ACTIONS_CLASS } from "./GoalCardActions";
import { GoalCardFacts } from "./GoalCardFacts";
import { GoalCardHeader, alignWithGoalName } from "./GoalCardHeader";
import { getGoalCardFacts } from "./utils/goalCardFacts";

const ACTIONS = `.${GOAL_CARD_ACTIONS_CLASS}`;

const MAX_WIDTH = 600;

type Props = {
  goal: GoalOverview;
  onClick?: () => void;
};

export const GoalCard = ({ goal, onClick }: Props) => {
  const { t } = useResource();
  const isTablet = useResponsive(TABLET_MAX_WIDTH);
  const facts = getGoalCardFacts(goal, t);
  const isCompleted = goal.progressStatus === GoalOverviewProgressStatus.completed;

  return (
    <Stack
      maxWidth={MAX_WIDTH}
      gap={Spacing.XL}
      p={Spacing.XL}
      bgcolor="background.paper"
      border={1}
      borderColor="grey.400"
      borderRadius={Radius.L}
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 150ms",
        "&:hover": { borderColor: "primary.main" },
        [`& ${ACTIONS}`]: { opacity: 0, transition: "opacity 150ms" },
        [`&:hover ${ACTIONS}, &:focus-within ${ACTIONS}`]: { opacity: 1 },
        "@media (hover: none)": { [`& ${ACTIONS}`]: { opacity: 1 } },
      }}
    >
      <GoalCardHeader goal={goal} onEdit={onClick} isTablet={isTablet} />

      {facts.length > 0 && (
        <>
          {/* runs past the content padding, up to Spacing.S before the card's edge */}
          <Box borderTop={1} borderColor="divider" mr={-Spacing.L} sx={(theme) => ({ ml: alignWithGoalName(theme) })} />
          <GoalCardFacts facts={facts} isCompleted={isCompleted} isTablet={isTablet} />
        </>
      )}
    </Stack>
  );
};
